import { chequeStatusLabel } from "@/utils/cheque-status";
import { apiFetch } from "..";
import type { SourceDocumentGroup } from "@/lib/api/ledgers";
import type { FormAction } from "@/types/resource-request";
import type { WithholdingType } from "@/lib/constants/withholding";
import type { DVRemarkCategory } from "@/lib/constants/dv-remark-category";

// User type for relations
interface BaseUser {
  id: number;
  name: string;
  email: string;
  position?: string;
}

// DEPRECATED: Use paymentMode.type instead (cash, bank, cheque)
// isCredit flag now replaces the "credit" mode
export type DVMode = "cash" | "bank" | "cheque" | "credit";

// DV Type — the three disbursement flows the board describes.
// PURCHASE_REQUEST is the legacy default; existing rows pre-migration are all this.
// ADVANCE pays cash ahead to a payee (a vendor; employees are vendors too), booked to the
// account chosen on it, and closed by a liquidation report or by applying it to the
// payee's bills. CASH_ADVANCE is a payroll cash advance's voucher, made from Payroll.
export type DVType = "PAYMENT_REQUEST" | "PURCHASE_REQUEST" | "REPLENISHMENT" | "CASH_ADVANCE" | "ADVANCE";

// Cheque lifecycle statuses, owned directly by the DV (cheque mode). Null until the DV is
// approved (then "pending"). No "deposited" — clearing doesn't move the bank balance.
export type ChequeStatus = "pending" | "cleared" | "bounced" | "cancelled";

export const DV_TYPE_LABELS: Record<DVType, string> = {
  PAYMENT_REQUEST: "Payment Request",
  PURCHASE_REQUEST: "Purchase Request",
  REPLENISHMENT: "Replenishment",
  CASH_ADVANCE: "Salary Advance",
  ADVANCE: "Advance",
};

export const DV_TYPE_DESCRIPTIONS: Record<DVType, string> = {
  PAYMENT_REQUEST: "Future payment — not yet paid",
  PURCHASE_REQUEST: "Procurement disbursement (with or without PO)",
  REPLENISHMENT: "Reimbursement — already paid out of pocket",
  CASH_ADVANCE: "A payroll cash advance, deducted from pay",
  ADVANCE: "Cash paid ahead, liquidated or applied to the bill later",
};

/** A supplier advance voucher's standing: what it advanced, applied and has left (DV detail). */
export interface SupplierAdvanceState {
  amount: number;
  applied: number;
  remaining: number;
  /** In the books (approved or completed): only then can it be applied. */
  inBooks: boolean;
  applications: Array<{ id: number; amount: number; appliedAt: string; payableVoucher: { id: number; number: string } }>;
}

// ── Cash advances (server lib/cash-advance-tracking.ts) ──
export type CashAdvanceKind = "SALARY" | "LIQUIDATION";
export type CashAdvanceStatus = "DRAFT" | "OPEN" | "PARTLY_LIQUIDATED" | "LIQUIDATED" | "DEDUCTED_FROM_PAY" | "CANCELLED";

export interface CashAdvanceEmployee {
  id: number;
  firstName: string;
  middleName: string | null;
  lastName: string;
  employeeNumber: string | null;
}

/** What a cash advance gave, what took it in, and what is left. Worked out on request. */
export interface CashAdvanceState {
  disbursementVoucherId: number;
  number: string;
  kind: CashAdvanceKind;
  label: string;
  amount: number;
  releasedAt: string | null;
  liquidated: number;
  returned: number;
  /** Taken into reports not checked yet. */
  pending: number;
  remaining: number;
  available: number;
  status: CashAdvanceStatus;
  overdue: boolean;
  dueDate: string | null;
  ageDays: number | null;
  reports: { id: number; number: string; status: string; applied: number; liquidated: number; returned: number }[];
}

// ── Advances (server GET /api/disbursement-vouchers/advances) ──
/** ADVANCE: paid ahead to a payee, liquidated or applied to the bill. SALARY: a payroll advance, deducted from pay. */
export type AdvanceKind = "ADVANCE" | "SALARY";
/** One status for every advance. */
export type AdvanceListStatus = "DRAFT" | "OPEN" | "PARTLY_CLOSED" | "CLOSED" | "DEDUCTED_FROM_PAY" | "CANCELLED";

export interface AdvanceAccountRef {
  id: number;
  code: string;
  name: string;
}

/** One advance in the combined list. */
export interface AdvanceRow {
  disbursementVoucherId: number;
  number: string;
  kind: AdvanceKind;
  /** A vendor; an EMPLOYEE only on a salary advance or one from before there was one Advance type. */
  payee: { type: "VENDOR" | "EMPLOYEE"; id: number | null; name: string; code: string | null };
  project: { id: number; code: string; name: string } | null;
  description: string | null;
  voucherStatus: DisbursementVoucher["status"];
  releasedAt: string | null;
  amount: number;
  /** Liquidated, returned and applied to the payee's bills. */
  closed: number;
  remaining: number;
  dueDate: string | null;
  ageDays: number | null;
  overdue: boolean;
  status: AdvanceListStatus;
  /** The account the advance sits in (chosen on the voucher, else the posting account). */
  account: AdvanceAccountRef | null;
  /** What closed it: liquidation reports, or the payables it was applied to. */
  closedBy: { type: "LIQUIDATION_REPORT" | "PAYABLE_VOUCHER"; id: number; number: string; amount: number }[];
  payrollCashAdvance: { id: number; number: string } | null;
}

export interface AdvanceFilters {
  /** Comma list of ADVANCE, SALARY; default ADVANCE. */
  kinds?: string;
  /** OUTSTANDING (open or partly closed), OVERDUE, CLOSED, DRAFT, CANCELLED. */
  status?: "OUTSTANDING" | "OVERDUE" | "CLOSED" | "DRAFT" | "CANCELLED";
  vendorId?: number;
  projectId?: number;
  from?: string;
  to?: string;
  search?: string;
}

export interface AdvancesResponse {
  rows: AdvanceRow[];
  totals: {
    count: number;
    amount: number;
    closed: number;
    remaining: number;
    overdue: number;
  };
}

/** The account an Advance to a payee starts from, and where that came from. */
export interface AdvanceAccountDefault {
  account: AdvanceAccountRef | null;
  /** CLASSIFICATION: the payee's classification (its name); POSTING_ACCOUNT: Advances to Supplier. */
  source: { type: "CLASSIFICATION" | "POSTING_ACCOUNT"; name: string } | null;
}

// DV Line Item types
export interface DisbursementVoucherItem {
  id: number;
  number: number;
  projectId: number | null;
  project?: { id: number; code: string; name: string } | null;
  financeCodeId: number | null;
  financeCode?: { id: number; code: string; name: string } | null;
  itemId?: number | null;
  description: string;
  /** What the line was expected to cost — the PO's figure on an imported row. */
  amount: number;
  /** What it ACTUALLY cost, once known. null means the line has not been revised. */
  revisedAmount?: number | null;
  /** The withholding code this line is taxed under, or null when it isn't withheld from. */
  withholdingType?: WithholdingType | null;
  /**
   * Snapshots stamped by the server from the Posting Setup when the line was set —
   * PERCENTS (Decimal, so they may arrive as strings). Null on old rows.
   */
  withholdingPercent?: number | string | null;
  atc?: string | null;
  vatRate?: number | string | null;
  /** Whether this line's amount includes VAT. */
  isVat?: boolean;
  sourcePurchaseOrderId?: number | null;
  sourcePurchaseOrder?: { id: number; number: string; totalAmount?: number | null; paymentStatus?: string | null; selectedVendorId?: number | null } | null;
  sourcePurchaseItemId?: number | null;
  /** The payment release line this item pays, on a voucher that pays several lines with one cheque. */
  paymentReleaseLineId?: number | null;
  sourcePurchaseItem?: {
    id: number;
    item?: { id: number; name: string } | null;
  } | null;
}

export interface CreateDVItemData {
  // Optional — the DV header carries the single project for the whole voucher
  // (1 DV : 1 project). The backend overwrites any per-item value with the header's.
  projectId?: number;
  financeCodeId: number;
  itemId?: number | null;
  description: string;
  /** What the line was expected to cost. */
  amount: number;
  /**
   * What it ACTUALLY cost, when already known at save time. null/undefined = not revised.
   *
   * Declared here as well as on DVItemPayload because this is the wire contract: without
   * it the field travelled only by accident of structural typing, and nothing would have
   * caught its removal.
   */
  revisedAmount?: number | null;
  /**
   * The expanded-withholding code this line is taxed under, or null for a line nobody
   * withholds from. One voucher can pay for goods AND services, each at its own rate and
   * each certified under its own ATC on the BIR 2307.
   *
   * Null IS the "not taxed" answer — there is no separate flag beside it to disagree with.
   */
  withholdingType?: WithholdingType | null;
  /** Whether THIS line's amount includes the 12% VAT. A voucher can mix the two — a VAT
   *  supplier's invoice beside a non-VAT reimbursement. */
  isVat?: boolean;
  sourcePurchaseOrderId?: number;
  sourcePurchaseItemId?: number;
  /**
   * The payment release line this item pays, on a voucher that pays several
   * lines of one release with one cheque. It has to survive every save: it is
   * what says how much of the voucher goes against which payable.
   */
  paymentReleaseLineId?: number | null;
}

export interface DisbursementVoucher {
  /** Set when this voucher pays payables: it is the net payment, the tax is theirs. */
  payableTax?: import("@/types/disbursement-voucher").DvPayableTax | null;
  /** Approved, or approved on submit, through its payment release (server lib/release-approval-cover.ts). */
  releaseCover?: import("@/types/disbursement-voucher").DvReleaseCover | null;
  /** The funding account of the payment release it pays, used when it names none of its own. */
  releaseFunding?: import("@/types/disbursement-voucher").DvReleaseFunding | null;
  id: number;
  projectId: number | null;
  number: string;
  payeeName: string | null;
  description: string | null;
  approvedBy: number | null;
  reviewedBy: number | null;
  receivedBy: number | null;
  completedBy: number | null;
  // "Paid pending receipt" — payment went out while the DV stays "approved" (receipt pending).
  paidBy: number | null;
  paidAt: string | null;
  // "Marked as paid, receipt pending": a person's note, identification only. The
  // books follow paidAt (set at approval, or when the cheque clears).
  markedPaidBy?: number | null;
  markedPaidAt?: string | null;
  paidMarker?: { id: number; name: string } | null;
  paymentModeId: number | null;
  paymentReferenceNumber: string | null;
  referenceNumber: string | null;
  amount: number | null;
  paymentDate: string | null;
  status: "draft" | "for review" | "pending approval" | "approved" | "rejected" | "cancelled" | "completed" | null;
  s3FilePath: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  approvedAt: string | null;
  completedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: number | null;
  updatedBy: number | null;
  // Credit payment flag
  isCredit: boolean;
  // VAT-inclusive flag (mirrors PurchaseOrder.isVat)
  isVat: boolean;
  // Withholding tax (only applicable when isVat is true). Rate is derived from the type.
  withholdingTax: boolean;
  withholdingType: WithholdingType | null;
  // DV type — see DVType
  dvType: DVType;
  /** A Cash Advance voucher: the employee it is paid to. */
  employeeStaffId?: number | null;
  employee?: CashAdvanceEmployee | null;
  /** An advance: the account it debits (null = the posting account). */
  advanceAccountId?: number | null;
  advanceAccount?: AdvanceAccountRef | null;
  /** Set on a payroll cash advance's voucher (deducted from pay, never liquidated). */
  cashAdvanceId?: number | null;
  cashAdvance?: { id: number; number: string } | null;
  /** The advance's state, on the detail of a Cash Advance voucher; null otherwise. */
  cashAdvanceState?: CashAdvanceState | null;
  /** On the detail of a Supplier Advance voucher; null otherwise. */
  supplierAdvance?: SupplierAdvanceState | null;
  // Replenishment-only fields (null for other types)
  liquidationReportId: number | null;
  liquidationPeriodFrom: string | null;
  liquidationPeriodTo: string | null;
  // Manual float override — only ever set when the linked liquidation report resolves to
  // zero floats on its own (see ReplenishmentFields / resolveLiquidationReportFloats).
  floatId: number | null;
  paidExternallyAt: string | null;
  expenseReceiptUrl: string | null;
  hasForm2307: boolean;
  preparedByHr: boolean;
  // DEPRECATED: DV Mode (use paymentMode.type instead)
  dvMode: DVMode | null;
  // Bank mode fields
  bankName: string | null;
  accountName: string | null;
  accountNumber: string | null;
  swiftCode: string | null;
  transferredTo: string | null;
  // Bank mode - intermediary personnel (optional)
  intermediaryName: string | null;
  // Cheque mode fields (DV-owned cheque lifecycle)
  chequeNumber: string | null;
  chequeIssueDate: string | null;
  chequeStatus: ChequeStatus | null;
  chequeDepositedAt: string | null;
  /** The day the cheque was handed to its payee. It stays "pending" until it clears. */
  chequeReleasedAt: string | null;
  chequeClearedAt: string | null;
  chequeBouncedAt: string | null;
  chequeCancelledAt: string | null;
  bankAccountId: number | null;
  bankAccount?: { id: number; accountCode: string; bankName: string; accountName: string } | null;
  // Cash mode fields
  receivedByName: string | null;
  // Vendor link
  vendorId: number | null;
  vendorBankAccountId: number | null;
  // Relations
  project?: { id: number; name: string; code: string };
  vendor?: {
    id: number;
    name: string;
    contactNumber: string | null;
    location?: {
      name: string;
      street: string | null;
      barangay: string | null;
      city: string | null;
      province: string | null;
    } | null;
    bankAccounts?: Array<{
      id: number;
      bankName: string;
      accountNumber: string;
      accountName: string | null;
      isDefault: boolean;
    }>;
  } | null;
  vendorBankAccount?: {
    id: number;
    bankName: string;
    accountNumber: string;
    accountName: string | null;
  } | null;
  approver?: BaseUser;
  reviewer?: BaseUser;
  receiver?: BaseUser;
  completer?: BaseUser;
  payer?: BaseUser;
  paymentMode?: { id: number; mode: string; type: string; hasReferenceNumber?: boolean };
  purchaseOrder?: { id: number; number: string };
  disbursementVoucherPurchaseOrders?: Array<{
    purchaseOrder: {
      id: number;
      number: string;
      title: string | null;
      payeeName: string;
      totalAmount: number;
      status: string;
      purchaseItems?: Array<{
        id: number;
        quantity: number | null;
        unitPrice: number | null;
        amount: number | null;
        isVatPrice: boolean | null;
        item?: {
          id: number;
          name: string;
          brand: string | null;
          description: string | null;
          unit?: { id: number; name: string } | null;
        } | null;
        project?: { id: number; name: string; code: string } | null;
        financeCode?: { id: number; code: string; name: string } | null;
      }>;
    };
  }>;
  disbursementVoucherProjects?: Array<{ project: { id: number; name: string; code: string } }>;
  items?: DisbursementVoucherItem[];
  /**
   * Whether the unspent balance came back, when the revised items total LESS than
   * `amount`. null means the question does not arise — nothing revised, or nothing left
   * over. It is what decides the expense the system books.
   */
  changeReturned?: boolean | null;
  creator?: BaseUser;
  updater?: BaseUser;
  // Per-type uploaded-file counts (from the list/detail endpoints). Powers the
  // "has attachment" indicator + its hover breakdown in the summary list.
  _count?: {
    attachments: number;
    expenseReceipts: number;
    form2307Files: number;
    proofImages: number;
  };
}

// ─── Payment-mode display details ───────────────────────────────────────────
// Single source of truth for which DV fields each payment-mode TYPE shows in the
// read-only surfaces (view modal + PDF). To support a new mode type later, add an
// entry here — the modal and PDF render whatever this returns. The mode-agnostic
// Recipient / Received By fields are handled separately and aren't listed here.
type PaymentModeDetailField = {
  label: string;
  key: keyof DisbursementVoucher;
  optional?: boolean; // omit the row when the value is empty
};

export const PAYMENT_MODE_DETAIL_FIELDS: Record<string, PaymentModeDetailField[]> = {
  bank: [
    { label: "Bank Name", key: "bankName" },
    { label: "Account Name", key: "accountName" },
    { label: "Account Number", key: "accountNumber" },
    { label: "SWIFT Code", key: "swiftCode", optional: true },
    { label: "Transferred To", key: "transferredTo" },
  ],
  cheque: [
    { label: "Cheque Number", key: "chequeNumber" },
    // Pay-to account name written on the cheque (shared accountName column).
    { label: "Account Name", key: "accountName", optional: true },
  ],
  cash: [],
};

export interface PaymentModeDetailRow {
  label: string;
  value: string;
}

/**
 * Resolve a DV into the ordered detail rows for its payment-mode type. Empty
 * optional fields are skipped. For cheque, the linked Post-Dated Cheque (number +
 * status) is appended when present. Returns [] when the mode carries no details
 * (e.g. cash) — callers should then render nothing.
 */
export function getPaymentModeDetails(dv: DisbursementVoucher): PaymentModeDetailRow[] {
  const type = dv.paymentMode?.type ?? "";
  const fields = PAYMENT_MODE_DETAIL_FIELDS[type] ?? [];

  // For bank mode, fall back to the linked vendor bank account when the DV's own scalar
  // fields are empty (legacy DVs never copied bankName/accountName/accountNumber into their
  // own columns). Precedence: explicit vendorBankAccount → vendor default → first account.
  const fallbackBank =
    type === "bank"
      ? dv.vendorBankAccount ||
        dv.vendor?.bankAccounts?.find((a) => a.isDefault) ||
        dv.vendor?.bankAccounts?.[0] ||
        null
      : null;
  const bankFallbackByKey: Partial<Record<keyof DisbursementVoucher, string | null | undefined>> = {
    bankName: fallbackBank?.bankName,
    accountName: fallbackBank?.accountName,
    accountNumber: fallbackBank?.accountNumber,
  };
  // Transferred To and Payee are the same entity → backfill from payee on legacy DVs.
  const payeeName = dv.payeeName || dv.vendor?.name || "";

  const rows: PaymentModeDetailRow[] = [];
  for (const f of fields) {
    const raw = dv[f.key];
    let value = raw == null ? "" : String(raw);
    if (!value && f.key in bankFallbackByKey) {
      const fb = bankFallbackByKey[f.key];
      if (fb) value = String(fb);
    }
    if (!value && f.key === "transferredTo") value = payeeName;
    if (!value && f.optional) continue;
    rows.push({ label: f.label, value: value || "—" });
  }

  // Cheque mode: append DV-owned cheque detail rows (issue date, status, drawn-from bank).
  if (type === "cheque") {
    if (dv.chequeIssueDate) {
      rows.push({ label: "Cheque Date", value: new Date(dv.chequeIssueDate).toLocaleDateString() });
    }
    if (dv.chequeStatus) {
      rows.push({ label: "Cheque Status", value: chequeStatusLabel(dv.chequeStatus, dv.chequeReleasedAt) });
    }
    if (dv.chequeReleasedAt) {
      rows.push({ label: "Cheque Released", value: new Date(dv.chequeReleasedAt).toLocaleDateString() });
    }
    if (dv.chequeStatus === "cleared" && dv.chequeClearedAt) {
      rows.push({ label: "Cheque Cleared", value: new Date(dv.chequeClearedAt).toLocaleDateString() });
    }
    if (dv.bankAccount?.bankName) {
      rows.push({
        label: "Drawn From",
        value: [dv.bankAccount.bankName, dv.bankAccount.accountName].filter(Boolean).join(" - "),
      });
    }
  }

  return rows;
}

export interface DisbursementVouchersResponse {
  disbursementVouchers: DisbursementVoucher[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** The list's filters, shared by the Headers, Details and Journal entries views. */
export interface DisbursementVoucherListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  projectId?: number;
  paymentModeId?: number;
  dvType?: DVType;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

/** `?a=1&b=2` from the defined params, "" when there are none. */
const listQuery = (params?: object) => {
  const entries = Object.entries(params ?? {})
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => [k, String(v)]);
  return entries.length > 0 ? `?${new URLSearchParams(entries).toString()}` : "";
};

export interface CreateDisbursementVoucherData {
  projectId?: number;
  projectIds?: number[];
  purchaseOrderId?: number;
  purchaseOrderIds?: number[];
  payeeName: string;
  description?: string;
  amount?: number; // Optional - calculated from items if not provided
  /**
   * Where the unspent balance went, when revised line amounts bring the cost under the
   * amount being released. null = the question does not arise; see the field on
   * `DisbursementVoucher` for what the answer decides.
   */
  changeReturned?: boolean | null;
  paymentModeId: number;
  paymentReferenceNumber?: string;
  referenceNumber?: string;
  reviewedBy?: number;
  approvedBy?: number;
  // Credit payment flag (replaces dvMode: "credit")
  isCredit?: boolean;
  // VAT-inclusive flag
  isVat?: boolean;
  // Withholding tax (only valid when isVat is true)
  withholdingTax?: boolean;
  withholdingType?: WithholdingType | null;
  // DV type
  dvType?: DVType;
  /** Required for CASH_ADVANCE: the employee (Staff id). The server sets the payee to their name. */
  employeeStaffId?: number | null;
  /** An advance (cash advance for liquidation, supplier advance): the account it debits. */
  advanceAccountId?: number | null;
  // Replenishment-only fields. For REPLENISHMENT, link a Liquidation Report — the server
  // derives payee/period/amount from it.
  liquidationReportId?: number | null;
  liquidationPeriodFrom?: string | null;
  liquidationPeriodTo?: string | null;
  paidExternallyAt?: string | null;
  expenseReceiptUrl?: string | null;
  hasForm2307?: boolean;
  preparedByHr?: boolean;
  // DEPRECATED: DV Mode (use paymentMode.type instead)
  dvMode?: DVMode;
  // Bank mode fields (when paymentMode.type === "bank")
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  swiftCode?: string;
  transferredTo?: string;
  intermediaryName?: string | null;
  // Cheque mode fields (when paymentMode.type === "cheque"). DV owns the cheque.
  chequeNumber?: string;
  chequeIssueDate?: string | null; // post-dated/issue date
  bankAccountId?: number | null; // drawn-from company account (required for cheque mode)
  // Cash/Cheque/Credit mode fields (when paymentMode.type === "cash" or "cheque", or isCredit)
  receivedByName?: string;
  // Vendor bank account link
  vendorId?: number | null;
  vendorBankAccountId?: number | null;
  // Line items
  items?: CreateDVItemData[];
}

export interface UpdateDisbursementVoucherData {
  payeeName?: string;
  description?: string;
  amount?: number;
  /** See CreateDisbursementVoucherData. null clears a stale answer. */
  changeReturned?: boolean | null;
  paymentModeId?: number;
  paymentReferenceNumber?: string | null;
  referenceNumber?: string;
  reviewedBy?: number;
  approvedBy?: number;
  // Header project for the DV (1 DV : 1 project)
  projectId?: number;
  // Credit payment flag
  isCredit?: boolean;
  // VAT-inclusive flag
  isVat?: boolean;
  // Withholding tax (only valid when isVat is true)
  withholdingTax?: boolean;
  withholdingType?: WithholdingType | null;
  // DV type
  dvType?: DVType;
  /** Required for CASH_ADVANCE: the employee (Staff id). The server sets the payee to their name. */
  employeeStaffId?: number | null;
  /** An advance (cash advance for liquidation, supplier advance): the account it debits. */
  advanceAccountId?: number | null;
  // Replenishment-only fields. For REPLENISHMENT, link a Liquidation Report — the server
  // derives payee/period/amount from it.
  liquidationReportId?: number | null;
  liquidationPeriodFrom?: string | null;
  liquidationPeriodTo?: string | null;
  paidExternallyAt?: string | null;
  expenseReceiptUrl?: string | null;
  hasForm2307?: boolean;
  preparedByHr?: boolean;
  // DEPRECATED: DV Mode (use paymentMode.type instead)
  dvMode?: DVMode;
  // Project IDs for TBL liquidation
  projectIds?: number[];
  // Bank mode fields (when paymentMode.type === "bank")
  bankName?: string;
  accountName?: string;
  accountNumber?: string;
  swiftCode?: string;
  transferredTo?: string;
  intermediaryName?: string | null;
  // Cheque mode fields (when paymentMode.type === "cheque")
  chequeNumber?: string;
  chequeIssueDate?: string | null;
  bankAccountId?: number | null;
  // Cash/Cheque/Credit mode fields
  receivedByName?: string;
  // Vendor bank account link
  vendorId?: number | null;
  vendorBankAccountId?: number | null;
  // Line items
  items?: CreateDVItemData[];
}

export interface WorkflowActionData {
  comment?: string;
}

export interface ExpenseSummary {
  totalAmount: number;
  totalCount: number;
  averageAmount: number;
  byPaymentMode: Array<{
    paymentModeId: number;
    paymentMode?: { id: number; mode: string; code: string | null };
    totalAmount: number;
    count: number;
  }>;
  byProject: Array<{
    projectId: number;
    project?: { id: number; name: string; code: string };
    totalAmount: number;
    count: number;
  }>;
  byStatus: Array<{
    status: string;
    count: number;
    totalAmount: number;
  }>;
}

// Expense summary by finance code (Chart of Accounts) - 3-level hierarchy
export interface ExpenseNode {
  id: number;
  code: string;
  name: string;
  level: number;
  parentId: number | null;
  totalAmount: number;
  itemCount: number;
  dvCount: number;
  children: ExpenseNode[];
}

export interface ExpenseSummaryByCode {
  tree: ExpenseNode[];
  grandTotal: number;
  codesWithExpenses: number;
  totalCodes: number;
  filters: {
    startDate?: string;
    endDate?: string;
    projectId?: number;
    status?: string;
  };
}

// Legacy types for backwards compatibility (deprecated)
export interface ExpenseSummaryByCodeItem {
  financeCodeId: number;
  financeCode: {
    id: number;
    code: string;
    name: string;
    level: number;
  };
  totalAmount: number;
  itemCount: number;
  dvCount: number;
}

export interface ExpenseSummaryByCodeCategory {
  category: string;
  categoryCode: string;
  total: number;
  children: ExpenseSummaryByCodeItem[];
}

export interface DVItemsByFinanceCode {
  financeCode: { id: number; code: string; name: string };
  totalAmount: number;
  items: Array<{
    id: number;
    description: string;
    amount: number;
    disbursementVoucher: {
      id: number;
      number: string;
      payeeName: string | null;
      status: string | null;
      createdAt: string;
    };
    project?: { id: number; code: string; name: string } | null;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface DisbursementVoucherProof {
  id: number;
  disbursementVoucherId: number;
  s3FilePath: string;
  fileName: string;
  createdAt: string;
  uploadedBy: number;
  url: string;
}

// The route segment for each DV file kind (matches the backend endpoints).
export type DVFileRoute = "attachments" | "expense-receipts" | "form-2307";

/** What a voucher's BIR Form 2307 will print, shown before the PDF is opened. */
export interface Bir2307Summary {
  /** Null until the supplier is marked as an individual or a corporation. */
  atc: string | null;
  rate: number;
  incomePayment: number;
  taxWithheld: number;
  /**
   * YYYY-MM-DD — Item 1 of the form as it will print. The quarter the payment falls in,
   * unless someone has typed a period on the certificate.
   */
  periodFrom: string;
  periodTo: string;
  /** That quarter, always — what "use the payment's quarter" goes back to. */
  quarterFrom: string;
  quarterTo: string;
  /** True when the printed period was typed rather than derived from the payment date. */
  periodIsCustom: boolean;
  /**
   * Which of Part III's three month columns the amount sits in (0, 1 or 2) — the
   * payment's month within its own quarter, not within the printed period.
   */
  monthIndex: 0 | 1 | 2;
}

/** A detail the certificate needs that nobody has filled in yet. */
export type Bir2307MissingField = "payee" | "tin" | "whtBusinessGroup" | "address" | "zip";

/**
 * The same gaps `problems` describes in words, as fields with their current values and
 * where they are stored — so the certificate's own screen can offer them instead of
 * naming three other pages.
 *
 * The split matters to the person filling them in: the COMPANY's details are settings
 * rows, filled in once for every 2307 the company will ever issue. The SUPPLIER's are
 * once per supplier — and its address and ZIP live on the shared location, so filling
 * them in fixes every other vendor registered at the same address.
 */
export interface Bir2307Setup {
  company: {
    missing: Bir2307MissingField[];
    tin: string | null;
    address: string | null;
    zip: string | null;
  };
  supplier: {
    /** Null when the voucher names a typed-in payee with no Vendor Library row behind it. */
    vendorId: number | null;
    name: string | null;
    missing: Bir2307MissingField[];
    tin: string | null;
    /** The vendor's WTax business group (Posting Setup) — what decides the ATC. */
    whtBusinessGroupId: number | null;
    /** The location row the address and ZIP belong to — null when the vendor has none. */
    locationId: number | null;
    /** The registered address as the certificate prints it, joined. */
    address: string | null;
    zip: string | null;
    /** The same address in its own parts, so the modal can offer them as fields. */
    street: string | null;
    barangay: string | null;
    city: string | null;
    province: string | null;
  };
}

export type Bir2307Response =
  | { status: "not_applicable"; reason: string; url: null }
  // `url` is the FILED certificate — one page, stored, what Download and Print give.
  // `previewUrl` is what the screen shows: the same certificate with the BIR's own
  // guidelines page behind it, so the form reads as the BIR prints it.
  | { status: "incomplete"; summary: Bir2307Summary; problems: string[]; setup: Bir2307Setup; url: string | null; previewUrl: string }
  | { status: "ready"; summary: Bir2307Summary; url: string; previewUrl: string };

// A DV file attachment (image/PDF/Word) with an inline presigned URL.
export interface DVFile {
  id: number;
  fileName: string;
  mimeType: string;
  url: string;
  createdAt?: string; // used to tell "old" attachments from ones added during a change window
  uploadedBy?: number | null;
  /** Who put the file here. Shown because people outside the signature block can
   *  be given upload access, so the uploader is no longer implied. */
  uploadedByName?: string | null;
}

// "Request For Changes" on a Completed/Paid DV (approval workflow that unlocks attachments).
export type DVChangeRequestStatus = "PENDING" | "APPROVED" | "DENIED" | "COMPLETED";

export interface DVChangeRequest {
  id: number;
  disbursementVoucherId: number;
  reason: string;
  status: DVChangeRequestStatus;
  denyRemarks: string | null;
  createdAt: string;
  approvedAt: string | null;
  deniedAt: string | null;
  completedAt: string | null;
  creator: BaseUser | null;
  approver: BaseUser | null;
  denier: BaseUser | null;
  completer: BaseUser | null;
}

// One version of a remark's text/category, oldest-first when part of a `history` array.
export interface DVRemarkRevision {
  category: DVRemarkCategory | null;
  text: string;
  editedBy: BaseUser | null;
  editedAt: string;
}

// A remark "thread" on a DV — its current text/category plus the full edit history.
// Remarks are never deleted; editing inserts a new revision (see server model comment).
export interface DVRemark {
  id: number;
  category: DVRemarkCategory | null;
  text: string;
  createdBy: BaseUser | null;
  createdAt: string;
  /** Set only once the remark has been edited at least once. */
  lastEditedBy: BaseUser | null;
  lastEditedAt: string | null;
  /** Every revision, oldest first — history[0] is the original, history[last] is current. */
  history: DVRemarkRevision[];
}

export interface ReviseDVItemAmountsData {
  /**
   * One entry per line being set; `revisedAmount: null` clears a revision. Optional
   * because the two fields have different lifetimes: the amounts lock when the voucher is
   * submitted, while the change answer stays open until the payment settles — so the
   * change is usually sent on its own, with no lines at all.
   */
  items?: Array<{ id: number; revisedAmount: number | null }>;
  /** Omit to leave unchanged; null when there is no change to account for. */
  changeReturned?: boolean | null;
}

/** One line of a DV's accounting entry, with its account for display. */
export interface DvEntryLine {
  financeCodeId: number;
  code: string | null;
  name: string | null;
  debit: number;
  credit: number;
  particulars: string | null;
  partyType: "VENDOR" | "CLIENT" | "EMPLOYEE" | "OTHER" | null;
  partyId: number | null;
  partyName: string | null;
  partyTin: string | null;
}

/** A posted journal entry of the voucher (current, reversed, or a reversal). */
export interface DvPostedEntry {
  id: number;
  number: string;
  entryDate: string;
  status: string;
  isReversal: boolean;
  reversalReason: string | null;
  totalDebit: number;
  lines: Array<{ code: string; name: string; debit: number; credit: number; particulars: string | null; partyName: string | null }>;
}

export interface DvAccountingEntry {
  /** Built from the voucher as it is now — what posts unless edited. */
  suggested: { lines: DvEntryLine[]; issues: Array<{ severity: "BLOCK" | "WARN"; message: string }> };
  /** The accountant's entry; when present it is what posts. */
  manual: {
    lines: DvEntryLine[];
    editedAt: string | null;
    editedBy: string | null;
    /** The voucher changed after the edit: its lines, amounts, taxes or bank. The edited entry does not follow it. */
    outdated?: boolean;
  } | null;
  journal: DvPostedEntry[];
  /** The voucher posts only once paid. */
  /** The voucher is in the books: approved, paid or completed. (Named for when posting waited for payment.) */
  isPaid: boolean;
  postingEnabled: boolean;
}

/** What the add page sends to preview its entry: the parts of the form the entry depends on. */
export interface DvEntryPreviewPayload {
  dvType?: string;
  projectId?: number | null;
  vendorId?: number | null;
  payeeName?: string | null;
  employeeStaffId?: number | null;
  advanceAccountId?: number | null;
  paymentModeId?: number | null;
  bankAccountId?: number | null;
  paymentDate?: string | null;
  amount?: number | null;
  changeReturned?: boolean | null;
  purchaseOrderIds?: number[];
  liquidationReportId?: number | null;
  floatId?: number | null;
  paymentReleaseLineId?: number | null;
  items: Array<{
    financeCodeId?: number | null;
    itemId?: number | null;
    amount?: number | null;
    revisedAmount?: number | null;
    isVat?: boolean | null;
    withholdingType?: string | null;
    sourcePurchaseOrderId?: number | null;
  }>;
}

export interface DvEntryPreview {
  lines: DvEntryLine[];
  issues: Array<{ severity: "BLOCK" | "WARN"; message: string }>;
}

export interface PayeeSuggestions {
  bankAccounts: Array<{ bankName: string; accountName: string; accountNumber: string; uses: number }>;
  chequeNames: Array<{ name: string; uses: number }>;
}

export interface SaveDvEntryLine {
  financeCodeId: number;
  debit: number;
  credit: number;
  particulars?: string | null;
  partyType?: DvEntryLine["partyType"];
  partyId?: number | null;
  partyName?: string | null;
  partyTin?: string | null;
}

export const disbursementVouchersApi = {
  /** The voucher's accounting entry: suggested, edited, and what is posted. */
  getAccountingEntry: (id: number) =>
    apiFetch<DvAccountingEntry>(`/api/disbursement-vouchers/${id}/accounting-entry`),
  /**
   * `baseEditedAt` is when the entry being replaced was last edited (null =
   * the suggested entry). The server refuses with 409 if someone changed it
   * since, instead of overwriting their edit.
   */
  /** What earlier vouchers to a payee who is not a vendor used: bank details and cheque names. */
  getPayeeSuggestions: (payeeName: string) =>
    apiFetch<PayeeSuggestions>(`/api/disbursement-vouchers/payee-suggestions?payeeName=${encodeURIComponent(payeeName)}`),
  /**
   * The entry a voucher WOULD post, from a form that is not saved yet. Built by
   * the rule that posts; reads only.
   */
  previewAccountingEntry: (payload: DvEntryPreviewPayload) =>
    apiFetch<DvEntryPreview>("/api/disbursement-vouchers/accounting-entry/preview", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  saveAccountingEntry: (id: number, lines: SaveDvEntryLine[], baseEditedAt: string | null) =>
    apiFetch<{ message: string }>(`/api/disbursement-vouchers/${id}/accounting-entry`, {
      method: "PUT",
      body: JSON.stringify({ lines, baseEditedAt }),
    }),
  resetAccountingEntry: (id: number, baseEditedAt: string | null) =>
    apiFetch<{ message: string }>(
      `/api/disbursement-vouchers/${id}/accounting-entry?baseEditedAt=${encodeURIComponent(baseEditedAt ?? "")}`,
      { method: "DELETE" }
    ),

  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    projectId?: number;
    paymentModeId?: number;
    dvType?: DVType;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
  }) => {
    const queryString = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()}`
      : "";
    return apiFetch<DisbursementVouchersResponse>(`/api/disbursement-vouchers${queryString}`);
  },

  /** The list as a ledger (Details and Journal entries): each voucher with its lines and the journal entries it posted. */
  getLedger: (params?: DisbursementVoucherListParams) =>
    apiFetch<{
      documents: SourceDocumentGroup[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(`/api/disbursement-vouchers/ledger${listQuery(params)}`),

  getList: () => apiFetch<DisbursementVoucher[]>("/api/disbursement-vouchers/list"),

  /** Every cash advance with its state, for the tracker. */
  getAdvances: (filters?: AdvanceFilters) =>
    apiFetch<AdvancesResponse>(`/api/disbursement-vouchers/advances${listQuery(filters)}`),

  /** The account an Advance to this payee starts from. */
  getAdvanceAccount: (vendorId: number | null) =>
    apiFetch<AdvanceAccountDefault>(`/api/disbursement-vouchers/advance-account${vendorId ? `?vendorId=${vendorId}` : ""}`),


  getStats: () =>
    apiFetch<{ status: string; count: number }[]>("/api/disbursement-vouchers/stats"),

  getExpenseSummary: (params?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    projectId?: number;
    paymentModeId?: number;
  }) => {
    const queryString = params
      ? `?${new URLSearchParams(
          Object.entries(params)
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        ).toString()}`
      : "";
    return apiFetch<ExpenseSummary>(`/api/disbursement-vouchers/expense-summary${queryString}`);
  },

  getById: (id: number) => apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}`),

  getByNumber: (number: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/by-number/${number}`),

  getDocument: (id: number) => apiFetch<{ url: string }>(`/api/disbursement-vouchers/${id}/document`),

  getBir2307: (id: number) => apiFetch<Bir2307Response>(`/api/disbursement-vouchers/${id}/bir-2307`),

  /**
   * Item 1 of the certificate, typed instead of derived. Both dates null clears it, and
   * the period goes back to following the voucher's payment date.
   */
  setBir2307Period: (id: number, period: { periodFrom: string | null; periodTo: string | null }) =>
    apiFetch<{ summary: Bir2307Summary | null }>(`/api/disbursement-vouchers/${id}/bir-2307/period`, {
      method: "PUT",
      body: JSON.stringify(period),
    }),

  getHistory: (id: number) => apiFetch<FormAction[]>(`/api/disbursement-vouchers/${id}/history`),

  create: (data: CreateDisbursementVoucherData) =>
    apiFetch<DisbursementVoucher>("/api/disbursement-vouchers", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  update: (id: number, data: UpdateDisbursementVoucherData) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: (id: number) =>
    apiFetch<{ message: string }>(`/api/disbursement-vouchers/${id}`, {
      method: "DELETE",
    }),

  submit: (id: number) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/submit`, {
      method: "POST",
    }),

  review: (id: number, data?: WorkflowActionData) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/review`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  approve: (id: number, data?: WorkflowActionData) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  reject: (id: number, data: WorkflowActionData & { comment: string }) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/reject`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  returnToDraft: (id: number, data: WorkflowActionData & { comment: string }) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/return-to-draft`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  // Terminal close-out of the voucher. Distinct from chequeCancel, which only
  // voids the cheque on a DV that remains otherwise live.
  cancel: (id: number, data: { comment: string }) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cancel`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  complete: (id: number, data?: { paymentDate?: string }) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/complete`, {
      method: "POST",
      body: JSON.stringify(data || {}),
    }),

  // Mark an approved DV as paid, pending receipt (stays "approved"; receipt pending).
  markPaid: (id: number) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/pay`, {
      method: "POST",
    }),

  updatePaymentDate: (id: number, paymentDate: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/payment-date`, {
      method: "PATCH",
      body: JSON.stringify({ paymentDate }),
    }),

  // Set the payment reference number (cheque/transfer/transaction no.) post-payment.
  updateReferenceNumber: (id: number, paymentReferenceNumber: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/reference-number`, {
      method: "PATCH",
      body: JSON.stringify({ paymentReferenceNumber }),
    }),

  // Record which company bank account paid a bank-transfer / e-wallet voucher. The
  // voucher form only asks for one on cheques, and the ledger cannot book the
  // payment without it. Allowed on paid and completed vouchers too.
  setBankAccount: (id: number, bankAccountId: number) =>
    apiFetch<{ id: number; bankAccountId: number; changed: boolean }>(`/api/disbursement-vouchers/${id}/bank-account`, {
      method: "PATCH",
      body: JSON.stringify({ bankAccountId }),
    }),

  // Set / correct the cheque number after the fact — the physical cheque is often written
  // days after the voucher is raised, so this avoids returning the DV for revision.
  // `reason` is required by the server when it replaces a number already on record.
  updateChequeNumber: (id: number, chequeNumber: string, reason?: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque-number`, {
      method: "PATCH",
      body: JSON.stringify({ chequeNumber, reason }),
    }),

  // Set / correct the cheque (issue/post) date after the fact — post-dated cheques get
  // re-dated when the payee asks to move the deposit, and the date is often only fixed
  // once the physical cheque is written. Same reasoning as updateChequeNumber above.
  // `reason` is required by the server when it replaces a date already on record.
  updateChequeDate: (id: number, chequeIssueDate: string, reason?: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque-date`, {
      method: "PATCH",
      body: JSON.stringify({ chequeIssueDate, reason }),
    }),

  // Record what the lines ACTUALLY cost, and/or where the unspent balance went. The PO's
  // estimate stays in `amount`; the actual lands in `revisedAmount`, so both stay visible.
  // Amounts are draft-only; the change answer stays open until the payment settles.
  reviseItemAmounts: (id: number, data: ReviseDVItemAmountsData) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/item-amounts`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // Cheque lifecycle actions (DV-owned). RELEASED = handed to the payee (a date; the cheque
  // stays pending); CLEARED = the bank paid it, and is final. Neither changes a balance.
  // Undoing a release takes a reason, which goes into the voucher's History.
  chequeRelease: (id: number) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque/release`, { method: "POST" }),
  chequeUnrelease: (id: number, reason?: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque/unrelease`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  chequeClear: (id: number) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque/clear`, { method: "POST" }),
  chequeBounce: (id: number, comment?: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque/bounce`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),
  chequeCancel: (id: number, comment?: string) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/cheque/cancel`, {
      method: "POST",
      body: JSON.stringify({ comment }),
    }),

  // Insufficient-funds scan: alerts when an account's balance is below its outstanding cheques.
  // (Path kept; semantics are now shortfall-based.)
  checkChequeDueDates: () =>
    apiFetch<{ message: string; accountsChecked: number; accountsShort: number; notificationsSent: number }>(
      "/api/disbursement-vouchers/cheque/check-due-dates",
      { method: "POST" },
    ),

  /**
   * The next cheque number(s) of ONE bank account: its latest number plus one,
   * and onwards for `count` cheques raised together. `chequeNumber` is null when
   * the account has no cheque on record yet, and the first one is typed.
   */
  getNextChequeNumber: (bankAccountId: number, count = 1) =>
    apiFetch<{ chequeNumber: string | null; chequeNumbers: string[]; lastChequeNumber: string | null }>(
      `/api/disbursement-vouchers/next-cheque-number?bankAccountId=${bankAccountId}&count=${count}`
    ),

  liquidate: (id: number, projectIds: number[]) =>
    apiFetch<DisbursementVoucher>(`/api/disbursement-vouchers/${id}/liquidate`, {
      method: "POST",
      body: JSON.stringify({ projectIds }),
    }),

  // Proof image methods
  uploadProof: async (dvId: number, file: File): Promise<DisbursementVoucherProof> => {
    const formData = new FormData();
    formData.append("proof", file);

    const { API_URL } = await import("@/constants");
    const response = await fetch(`${API_URL}/api/disbursement-vouchers/${dvId}/proof`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(error.error || "Failed to upload proof");
    }

    return response.json();
  },

  getProofs: (dvId: number) =>
    apiFetch<DisbursementVoucherProof[]>(`/api/disbursement-vouchers/${dvId}/proofs`),

  deleteProof: (dvId: number, proofId: number) =>
    apiFetch<{ message: string }>(`/api/disbursement-vouchers/${dvId}/proof/${proofId}`, {
      method: "DELETE",
    }),

  // ---- DV file attachments (general / expense receipts / 2307) ----
  // routeSegment ∈ "attachments" | "expense-receipts" | "form-2307".
  uploadDVFile: async (dvId: number, routeSegment: DVFileRoute, file: File): Promise<DVFile> => {
    const formData = new FormData();
    formData.append("file", file);
    const { API_URL } = await import("@/constants");
    const response = await fetch(`${API_URL}/api/disbursement-vouchers/${dvId}/${routeSegment}`, {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Upload failed" }));
      throw new Error(error.error || "Failed to upload file");
    }
    return response.json();
  },

  getDVFiles: (dvId: number, routeSegment: DVFileRoute) =>
    apiFetch<DVFile[]>(`/api/disbursement-vouchers/${dvId}/${routeSegment}`),

  deleteDVFile: (dvId: number, routeSegment: DVFileRoute, fileId: number) =>
    apiFetch<{ message: string }>(`/api/disbursement-vouchers/${dvId}/${routeSegment}/${fileId}`, {
      method: "DELETE",
    }),

  // ---- Request For Changes (on a Completed/Paid DV) ----
  getChangeRequests: (dvId: number) =>
    apiFetch<DVChangeRequest[]>(`/api/disbursement-vouchers/${dvId}/change-requests`),

  openChangeRequest: (dvId: number, reason: string) =>
    apiFetch<DVChangeRequest>(`/api/disbursement-vouchers/${dvId}/change-requests`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  approveChangeRequest: (changeRequestId: number) =>
    apiFetch<{ ok: true }>(`/api/disbursement-vouchers/change-requests/${changeRequestId}/approve`, {
      method: "POST",
    }),

  denyChangeRequest: (changeRequestId: number, remarks: string) =>
    apiFetch<{ ok: true }>(`/api/disbursement-vouchers/change-requests/${changeRequestId}/deny`, {
      method: "POST",
      body: JSON.stringify({ remarks }),
    }),

  // ---- Remarks (free-form notes, usable at any DV status) ----
  getRemarks: (dvId: number) =>
    apiFetch<DVRemark[]>(`/api/disbursement-vouchers/${dvId}/remarks`),

  addRemark: (dvId: number, data: { text: string; category?: DVRemarkCategory | null }) =>
    apiFetch<DVRemark[]>(`/api/disbursement-vouchers/${dvId}/remarks`, {
      method: "POST",
      body: JSON.stringify(data),
    }),

  editRemark: (dvId: number, remarkId: number, data: { text: string; category?: DVRemarkCategory | null }) =>
    apiFetch<DVRemark[]>(`/api/disbursement-vouchers/${dvId}/remarks/${remarkId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),

  // Set VAT / NON-VAT (and withholding tax) after the fact (DV must be approved).
  updateVat: (
    dvId: number,
    payload: { isVat: boolean; withholdingTax?: boolean; withholdingType?: WithholdingType | null },
  ) =>
    apiFetch<{
      id: number;
      isVat: boolean;
      withholdingTax: boolean;
      withholdingType: WithholdingType | null;
    }>(`/api/disbursement-vouchers/${dvId}/vat`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),

  /**
   * Export filtered DVs to an Excel workbook and trigger a browser download.
   * Filename comes from the server's Content-Disposition header (DV_Export_YYYY_MM_DD.xlsx).
   */
  exportToExcel: async (params?: {
    from?: string;
    to?: string;
    statuses?: string[];
    paymentModeId?: number;
    projectId?: number;
  }): Promise<void> => {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.append("from", params.from);
    if (params?.to) searchParams.append("to", params.to);
    if (params?.statuses && params.statuses.length > 0) {
      searchParams.append("statuses", params.statuses.join(","));
    }
    if (params?.paymentModeId) searchParams.append("paymentModeId", String(params.paymentModeId));
    if (params?.projectId) searchParams.append("projectId", String(params.projectId));
    const query = searchParams.toString();

    const { API_URL } = await import("@/constants");
    const response = await fetch(
      `${API_URL}/api/disbursement-vouchers/export${query ? `?${query}` : ""}`,
      { method: "GET", credentials: "include" }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Export failed" }));
      throw new Error(error.error || "Failed to export disbursement vouchers");
    }

    // Pull filename from Content-Disposition; fall back to today's date
    const disposition = response.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    let filename = match?.[1];
    if (!filename) {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      filename = `DV_Export_${y}_${m}_${day}.xlsx`;
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
